import type { GableRoofVerticalRails, GableRoofExtrusion } from "building-types/roofs/gableRoofExtrusionTypes";
import type { GenerateRoofVolumeOptions } from "building-types/roofVolumes/roofVolumeGenerationTypes";
import type { GeneratedRoofVolumeLegacy } from "building-types/roofVolumes/roofVolumeTypes";
import { GableRoofType, RoofBoundaryType, RoofPeakDirection } from "building-types/settings/settingsTypesEnum";
import { defaultRoofSettings } from "doc/settings/defaultSettings";
import { buildingEntityBaseNew } from "doc/utils/buildingEntityTools";
import {
  neighborRoofNew,
  neighborRoofsNew,
  roofPerimetersNew,
  roofExtensionsNew,
  shedRoofExtensionsNew,
  rectangularOverhangsNew,
  roofOverhangsNew,
  roofRails2DNew,
  roofGableFacesNew,
  roofRailsNew,
  roofConnectivityNew,
} from "doc/utils/roofTools";
import { aabb2New } from "geom/aabb2Types";
import { aabb3New } from "geom/aabb3Types";
import { planeNewXY } from "geom/plane";
import { polyLine2ByTwoPoints, polyLine2FromAABB2 } from "geom/polyLine2";
import { polyLine3ByTwoPoints } from "geom/polyLine3";
import { makeRoofHeelHeightOffsets } from "../../roofs/roofHeelHeightOffsets";
import { DEFAULT_ROOF_TRUNCATION_OPTIONS } from "../../roofs/roofTruncationDefaults";

const emptyNeighbor = neighborRoofNew(0);
const emptyNeigbors = Object.freeze(neighborRoofsNew(emptyNeighbor, emptyNeighbor, emptyNeighbor, emptyNeighbor));
const emptyAABB2 = aabb2New(-1, -1, 1, 1);
const emptyAABB3 = aabb3New(-1, -1, -1, 1, 1, 1);
const emptyPerimeter = polyLine2FromAABB2(emptyAABB2);
const emptyRail2 = polyLine2ByTwoPoints(-1, 0, 1, 0);
const emptyRail3 = polyLine3ByTwoPoints(-1, 0, 0, 1, 0, 0);

const emptyVerticalRails: GableRoofVerticalRails = {
  isStartTruncated: false,
  isStartFullyTruncated: false,
  truncatedStartHeight: 0,
  truncatedStartHeight2: 0,
  truncatedStartPeakDist: 0,
  isEndTruncated: false,
  isEndFullyTruncated: false,
  truncatedEndHeight: 0,
  truncatedEndHeight2: 0,
  truncatedEndPeakDist: 0,
  startLeftRail: emptyRail3,
  startRightRail: emptyRail3,
  endLeftRail: emptyRail3,
  endRightRail: emptyRail3,
  truncatedStartSlopes: [],
  truncatedEndSlopes: [],
  startBaseExtension: 0,
  endBaseExtension: 0,
};

const emptyExtrusion: GableRoofExtrusion = {
  leftFaces: [],
  rightFaces: [],
  startHipFaces: [],
  endHipFaces: [],
  startFaces: [],
  endFaces: [],
  bottomFace: null,
  startFace: null,
  endFace: null,
  convexVolumes: [],
  peakTextureV: 0,
};

/**
 * Returns an empty multi-gable roof.
 */
export function generatedRoofVolumeLegacyNew({
  roofId,
  roofData,
  index,
  name,
  polygonId,
  level,
  inputPerimeters,
  isDormer = false,
  blocks = [],
  subRoofBlocks = [],
}: GenerateRoofVolumeOptions): GeneratedRoofVolumeLegacy {
  const { parentId, roofVersion } = roofData;
  const { isFloatingVirtualRoof = false, isVirtualRoof = false } = inputPerimeters;

  return {
    ...buildingEntityBaseNew("ROOF_VOLUME_LEGACY", roofId, {
      type: "ROOF",
      id: roofId,
    }),
    id: roofId,
    roofSystemId: parentId,
    index,
    name,
    roofVersion,
    legacyRoof: false,
    legacyEaveReturns: false,
    legacyHeelHeightOffsets: false,
    isDormer,
    isFloatingVirtualRoof,
    isVirtualRoof,
    roofType: GableRoofType.DEFAULT,
    hasIndexedPeakDirection: false,
    boundaryType: RoofBoundaryType.BOUNDING_BOX,
    roofSettings: defaultRoofSettings,
    roofPlanSettings: {},
    roofElevationPlanSettings: {},
    polygonId,
    levelId: level.id,
    blocks,
    blockIds: subRoofBlocks,
    perimeters: roofPerimetersNew(
      0,
      RoofPeakDirection.HORIZONTAL,
      emptyPerimeter,
      emptyPerimeter,
      emptyPerimeter,
      emptyPerimeter,
      emptyPerimeter,
      emptyPerimeter,
      emptyPerimeter,
      emptyPerimeter,
      emptyPerimeter,
    ),
    overhangs: roofOverhangsNew({}),
    rectangularOverhangs: rectangularOverhangsNew(),
    span: 0,
    leftSpan: 0,
    rightSpan: 0,
    basePlane: planeNewXY(),
    baseHeight: 0,
    baseToPeak: 0,
    peakHeight: 0,
    ceilingHeight: 0,
    startEdge: 0,
    endEdge: 0,
    heelHeightOffsets: makeRoofHeelHeightOffsets(),
    verticalRails: emptyVerticalRails,
    extrusion: emptyExtrusion,
    extensions: roofExtensionsNew({}),
    shedExtensions: shedRoofExtensionsNew({}),
    neighborRoofs: emptyNeigbors,
    rails2D: roofRails2DNew(emptyRail2, emptyRail2, emptyRail2, emptyRail2, emptyRail2, emptyRail2),
    rails: roofRailsNew(emptyRail3, emptyRail3, emptyRail3, emptyRail3, emptyRail3, emptyRail3),
    isUnboundedShedRoof: false,
    startTruncation: DEFAULT_ROOF_TRUNCATION_OPTIONS,
    endTruncation: DEFAULT_ROOF_TRUNCATION_OPTIONS,
    baseSlopeOptions: [],
    baseSlopes: [],
    leftSideSlopes: [],
    rightSideSlopes: [],
    isStartFullyTruncated: false,
    isEndFullyTruncated: false,
    isRoofConnectedAtStart: false,
    isRoofConnectedAtEnd: false,
    shedRoofHalfFace: null,
    shedRoofWallHalfFace: null,
    aabb3: emptyAABB3,
    startFaces: roofGableFacesNew(),
    endFaces: roofGableFacesNew(),
    edges: [],
    exactEdges: [],
    eaveEdges: [],
    roofTopFaces: [],
    unclippedRoofTopFaces: [],
    roofTopFacesWithOverlap: [],
    clipBottomBSP: null,
    discardFriezeBSP: null,
    discardWallsBSP: null,
    discardStartWallBSP: null,
    discardEndWallBSP: null,
    roofTopBSP: null,
    startConnectivity: roofConnectivityNew({}),
    endConnectivity: roofConnectivityNew({}),
    exteriorWallOffsetsWithFinish: [],
    rectangularExtWallOffsetWithFinish: [],
    exteriorWallOffsetsFromOverhangDatum: [],
    rectangularExtWallOffsetsFromOverhangDatum: [],
  };
}
